import Decimal from 'decimal.js';
import { isNil } from 'lodash';
import { SystemRounding, UnitRounding } from '../types';

export type NumberLike = number | string | Decimal | null;

export function isDecimal(value: any): boolean {
  return value && typeof value.mul === 'function' && typeof value.toNumber === 'function';
}

export function asNumber(value?: NumberLike): number {
  if (!value) {
    return 0;
  }

  if (isDecimal(value)) {
    return (value as Decimal).toNumber();
  }

  if (typeof value === 'number') {
    return value;
  }

  return new Decimal(value).toNumber();
}

/** Returns `value` only when it is a real, finite number; otherwise `fallback`. A non-finite `Float` throws on GraphQL serialization and nulls the whole response. Unlike `asNumber`, this rejects `±Infinity` and lets the caller choose the fallback. */
export function finiteOr<T>(value: number | null | undefined, fallback: T): number | T {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

export function roundToNearestQuarter(value: number | string) {
  // Number(null) returns 0, and similarly undefined -> NaN, which is bad for idempotency.
  if (isNil(value)) {
    return value;
  }

  const valueAsNumber = Number(value);
  const rounded = Math.round(valueAsNumber * 4) / 4;
  return Number(rounded.toFixed(2));
}

export function roundToTwoDecimals(value: number) {
  if (isNil(value)) {
    return value;
  }

  const valueAsNumber = Number(value);

  return Math.round(valueAsNumber * 100) / 100;
}

export function getRoundingMethod(roundingMethod: UnitRounding | SystemRounding): (val: number) => number {
  switch (roundingMethod) {
    case 'NEAREST_QUARTER':
      return roundToNearestQuarter;
    case 'TWO_DECIMALS':
    default:
      return roundToTwoDecimals;
  }
}
